import { Prospect } from '@/types/prospect';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, Phone, Building, Mail, User } from 'lucide-react';
import { format } from 'date-fns';
import { useState } from 'react';
import { ProspectForm } from './ProspectForm';

interface ProspectCardProps {
  prospect: Prospect;
  onDelete: (id: string) => void;
  listId: string;
  readOnly?: boolean;
}

export function ProspectCard({ prospect, onDelete, listId, readOnly = false }: ProspectCardProps) {
  const [showEditForm, setShowEditForm] = useState(false);

  return (
    <>
      <Card className="group hover:shadow-md transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">{prospect.name}</CardTitle>
              <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                <Building className="h-3 w-3" />
                {prospect.company}
              </div>
            </div>
            <User className="h-5 w-5 text-muted-foreground" />
          </div>
        </CardHeader>
        
        <CardContent className="pb-3">
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="h-3 w-3" />
              <span className="font-mono">{prospect.phone}</span>
            </div>
            {prospect.email && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-3 w-3" />
                <span className="truncate">{prospect.email}</span>
              </div>
            )}
            {prospect.notes && (
              <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                {prospect.notes}
              </div>
            )}
          </div>
        </CardContent>
        
        <CardFooter className="pt-3 border-t">
          <div className="flex items-center justify-between w-full">
            <div className="text-xs text-muted-foreground">
              Added {format(new Date(prospect.createdAt), 'MMM d, yyyy')}
            </div>
            {!readOnly && (
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="sm" variant="ghost" onClick={() => setShowEditForm(true)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => onDelete(prospect.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardFooter>
      </Card>

      <ProspectForm
        open={showEditForm}
        onOpenChange={setShowEditForm}
        listId={listId}
        editingProspect={prospect}
      />
    </>
  );
}